import {
  type Pivot,
  SidebarNavList,
  SidebarNewChatRow,
} from "@/components/layout/SidebarNavList.tsx";
import { OkThxWordmark } from "@/components/icons/Logo.tsx";
import { HeaderUtilitySlot } from "@/components/layout/HeaderUtilitySlot.tsx";
import { panelTrailingIconClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { SplitPanelFrame } from "@/components/layout/SplitPanel/SplitPanelFrame.tsx";
import { SplitPanelHeader } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { Button } from "@/components/ui/button.tsx";
import { newChatSignalAtom } from "@/components/keyboard/navSignals.ts";
import { useSetAtom } from "jotai";
import type { ReactNode } from "react";
import { ThreadFilterDropdown, ThreadList } from "./ThreadList.tsx";

/**
 * Home's sidebar — its own header over the threads list, with the pivot row
 * naming what the canvas beside it can show. Self-contained: on desktop it is
 * the canvas split's left panel; on the mobile Chats tab it fills the surface on
 * its own.
 *
 * The `leading` (app-nav), `accountSwitcher`, and `sidebarToggle` slots hold
 * chrome the page owns and hands down; between them the header renders its own
 * title-slot content — the text-only "okthx" wordmark on desktop, the serif
 * filter dropdown on mobile.
 *
 * Desktop's header is the wordmark and the collapse toggle; every other action
 * sits where it can carry a label — New chat and Search in the nav list below,
 * settings in the `footer`. Mobile has no nav list and no second column, so it
 * reaches Search and starts a chat from the nav menu on the bar pinned to this
 * screen's bottom.
 */
export const ThreadListPanel = ({
  onSelect,
  isMobile,
  isWebView = false,
  leading,
  accountSwitcher,
  sidebarToggle,
  footer,
  activePivot = null,
  onSelectPivot,
  openThreadId = null,
}: {
  onSelect: (threadId: number) => void;
  isMobile: boolean;
  /** Embedded webviews suppress app chrome (here: the header wordmark). */
  isWebView?: boolean;
  leading?: ReactNode;
  accountSwitcher?: ReactNode;
  /** The control that collapses this panel; the page owns the state it toggles. */
  sidebarToggle?: ReactNode;
  /** Pinned below the list — see `SidebarFooter`. Omitted on mobile, which has
   *  no room for a shelf under a full-screen list. */
  footer?: ReactNode;
  /** Which canvas view the pivot row shows as current; `null` while a thread is. */
  activePivot?: Pivot | null;
  /** Omit to hide the pivot row — mobile has no canvas beside it to pivot. */
  onSelectPivot?: (pivot: Pivot) => void;
  /** The thread the canvas is showing, so its row reads as the open one. The
   *  counterpart to `activePivot`: exactly one of the two is ever set. */
  openThreadId?: number | null;
}) => {
  const requestNewChat = useSetAtom(newChatSignalAtom);

  const header = (
    <SplitPanelHeader>
      {leading}
      {isMobile ? (
        <div className="min-w-0">
          <ThreadFilterDropdown variant="title" />
        </div>
      ) : (
        !isWebView && (
          // Text-only "okthx" wordmark, glyph-free so the header holds a
          // single compact mark. Decorative, so hidden from a11y and suppressed
          // in embedded webviews like the rest of the app chrome;
          // `text-foreground` pins its currentColor fills to the theme token.
          //
          // A pixel height rather than a scale step: the cropped viewBox renders
          // the letterforms at ~83% of the box, so `h-5` would land the ink at
          // 16.7px, near enough to the 14px nav labels to read as one more row
          // than a mark. 21px puts it at 17.5px.
          <Button
            type="button"
            variant="blank"
            size="fit"
            onClick={() => requestNewChat((n) => n + 1)}
            // The mark stays decorative — its own SVG title would announce over
            // the button's name — so the button carries the name, and it names
            // the action rather than the brand.
            aria-label="New chat"
            data-statsig-name="nav-wordmark-new-chat"
            className="shrink-0"
          >
            <OkThxWordmark
              aria-hidden="true"
              hideGlyph
              className="h-[21px] w-auto shrink-0 text-foreground"
            />
          </Button>
        )
      )}
      {/* The collapse toggle takes the far corner on desktop, so the control
          that hides this panel is the last thing in it. */}
      <div className="ml-auto flex shrink-0 items-center gap-1">
        {accountSwitcher}
        {/* A phone shows one column, so this header is the page's upper right
            and the utilities belong in it, after the account chip. On a desktop
            this is the leftmost of three columns and the page beside it owns
            that corner — two publishers in the same layer, and the slot would
            fall to whichever happened to mount last. */}
        {isMobile && <HeaderUtilitySlot />}
        {/* Desktop-only: mobile has no second column to collapse.
            `panelTrailingIconClass` sits the glyph on the gutter rather than its
            box — the pull is on this wrapper, not the shared cluster, because on
            mobile that cluster ends in the account chip, which has no optical
            padding to cancel. */}
        {!isMobile && <div className={panelTrailingIconClass}>{sidebarToggle}</div>}
      </div>
    </SplitPanelHeader>
  );

  // Everything between the header and the footer scrolls as one column: the
  // views the canvas can show, the filter, and the threads under it. Only New
  // chat stays put with the header — it starts something rather than naming
  // somewhere, and it should not scroll out of reach. Mobile has no canvas
  // beside the list, so it gets neither.
  //
  // `ThreadList` owns the scroller and applies `--panel-gutter` inside it, so
  // only the pinned row needs the gutter here.
  const body = (
    <div className="flex flex-1 min-h-0 flex-col">
      {onSelectPivot && (
        // `pb-1` is the list's own row gap, so the pinned row keeps the rhythm
        // of the rows that scroll beneath it.
        <div className="shrink-0 px-(--panel-gutter) pb-1">
          <SidebarNewChatRow isActive={activePivot === "newchat"} onSelect={onSelectPivot} />
        </div>
      )}
      <ThreadList
        onSelect={onSelect}
        openThreadId={openThreadId}
        showFilterBar={!isMobile}
        leading={
          onSelectPivot ? (
            <div className="shrink-0 pb-4">
              <SidebarNavList activePivot={activePivot} onSelectPivot={onSelectPivot} />
            </div>
          ) : undefined
        }
      />
    </div>
  );

  return (
    <SplitPanelFrame header={header} footer={footer}>
      {body}
    </SplitPanelFrame>
  );
};
